import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App as CapacitorApp } from '@capacitor/app';
import Global from '../../Services/Common/Global';
import LogoImg from '../../Assets/Images/LogoWidth.svg';
import { MOK_FINDPW_RESULT_KEY } from './IdentityMokCallback';

function FindPw({
  open,
  onClose,
  findPwInputs,
  setFindPwInputs,
  SaveLogic4,
  loading,
}) {
  const [mokLoading, setMokLoading] = useState(false);
  const mokDeepLinkListenerRef = useRef(null);

  const ApplyMokResultToInputs = useCallback(
    (payload) => {
      if (!payload || typeof payload !== 'object') return;

      const name = payload.userName ?? '';
      const tel = (payload.userPhone ?? '').replace(/[^0-9]/g, '');

      setFindPwInputs((prev) => ({
        ...prev,
        identityConfirm: true,
        identityErrMsg: '',
        userNm: name,
        userTel: tel,
        userId: tel,
      }));
    },
    [setFindPwInputs]
  );

  useEffect(() => {
    if (!open) return;
    const stored = sessionStorage.getItem(MOK_FINDPW_RESULT_KEY);
    if (!stored) return;
    try {
      const result = JSON.parse(stored);
      ApplyMokResultToInputs(result);
    } catch (_) {
      // ignore
    } finally {
      sessionStorage.removeItem(MOK_FINDPW_RESULT_KEY);
    }
  }, [open, ApplyMokResultToInputs]);

  useEffect(() => {
    return () => {
      if (mokDeepLinkListenerRef.current) {
        mokDeepLinkListenerRef.current.remove();
        mokDeepLinkListenerRef.current = null;
      }
    };
  }, []);

  // 버튼 클릭 이벤트
  const HandlePrimaryAction = async () => {
    if (!findPwInputs.identityConfirm) {
      setMokLoading(true);
      setFindPwInputs((prev) => ({
        ...prev,
        identityErrMsg: '',
      }));

      try {
        if (Capacitor.isNativePlatform()) {
          const nativeReturnUrl = `${process.env.REACT_APP_MOK_RETURN_URL}/mok-callback?type=findpw&from=native`;

          if (mokDeepLinkListenerRef.current) {
            mokDeepLinkListenerRef.current.remove();
            mokDeepLinkListenerRef.current = null;
          }

          const mokRequestUrl = `${Global.base.useUrl}${Global.urls.Account.mokRequestPageUrl}?returnUrl=${encodeURIComponent(nativeReturnUrl)}`;
          const httpRes = await CapacitorHttp.get({ url: mokRequestUrl });
          const mokResData = httpRes.data;

          let nativeMokUrl = null;

          if (
            mokResData &&
            typeof mokResData === 'object' &&
            mokResData.encryptReqClientInfo
          ) {
            const sdkOrigin = new URL(process.env.REACT_APP_MOBILE_OK).origin;
            const mokBaseUrl = `${sdkOrigin}/ptb_mokauth.html`;
            const frontendHostname = new URL(
              process.env.REACT_APP_MOK_RETURN_URL
            ).hostname;
            const params = new URLSearchParams({
              usageCode: mokResData.usageCode || '01001',
              serviceId: mokResData.serviceId,
              convertInfo: 'undefined',
              returnUrl: mokResData.returnUrl,
              encryptVersion: 'undefined',
              encryptReqClientInfo: mokResData.encryptReqClientInfo,
              serviceType: mokResData.serviceType || 'telcoAuth',
              retTransferType: mokResData.retTransferType || 'MOKToken',
              callbackFunction: '',
              alternative: frontendHostname,
            });
            nativeMokUrl = `${mokBaseUrl}?${params.toString()}`;
          }

          if (!nativeMokUrl && typeof mokResData === 'string') {
            const doc = new DOMParser().parseFromString(
              mokResData,
              'text/html'
            );
            const form = doc.querySelector('form');
            if (form) {
              const formAction = form.getAttribute('action');
              const formParams = {};
              form.querySelectorAll('input').forEach((el) => {
                if (el.name) formParams[el.name] = el.value;
              });
              if (formAction && formParams.encryptReqClientInfo) {
                nativeMokUrl = `${formAction}?${new URLSearchParams(formParams).toString()}`;
              }
            }
          }

          if (!nativeMokUrl) {
            throw new Error('본인확인 URL을 가져오지 못했습니다.');
          }

          const handle = await CapacitorApp.addListener(
            'appUrlOpen',
            async (event) => {
              try {
                await Browser.close();
                if (!event.url.includes('mok-callback')) return;

                const url = new URL(event.url);
                const dataParam = url.searchParams.get('data');

                if (dataParam) {
                  const parsed = JSON.parse(dataParam);
                  if (
                    parsed?.resultCode &&
                    String(parsed.resultCode) !== '2000'
                  ) {
                    throw new Error(
                      parsed?.resultMsg || '본인확인에 실패했습니다.'
                    );
                  }
                  ApplyMokResultToInputs(parsed);
                } else {
                  throw new Error('본인확인 결과 정보가 없습니다.');
                }
              } catch (e) {
                setFindPwInputs((prev) => ({
                  ...prev,
                  identityErrMsg:
                    e?.message ||
                    '본인인증 처리에 실패했습니다. 다시 시도해 주세요.',
                }));
              } finally {
                if (mokDeepLinkListenerRef.current) {
                  mokDeepLinkListenerRef.current.remove();
                  mokDeepLinkListenerRef.current = null;
                }
                setMokLoading(false);
              }
            }
          );

          mokDeepLinkListenerRef.current = handle;
          await Browser.open({ url: nativeMokUrl });
          return;
        }

        const returnUrl = `${window.location.origin}/mok-callback?type=findpw`;
        const mokRequestUrl = `${Global.base.useUrl}${Global.urls.Account.mokRequestPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}`;

        if (window.MOBILEOK && typeof window.MOBILEOK.process === 'function') {
          window.MOBILEOK.process(mokRequestUrl, 'MB', '');
          setMokLoading(false);
          return;
        }

        throw new Error('본인확인 SDK가 로드되지 않았습니다.');
      } catch (e) {
        setMokLoading(false);
        setFindPwInputs((prev) => ({
          ...prev,
          identityErrMsg:
            e?.message || '본인인증 요청에 실패했습니다. 다시 시도해 주세요.',
        }));
      }
      return;
    }
    await SaveLogic4();
  };

  if (!open) return null;

  const primaryDisabled =
    mokLoading ||
    loading ||
    (findPwInputs.identityConfirm && !findPwInputs.userTel);

  const primaryLabel = findPwInputs.identityConfirm
    ? loading
      ? '발급 중...'
      : '임시 비밀번호 발급'
    : mokLoading
      ? '인증 요청 중...'
      : '전화번호 본인인증';

  return (
    <>
      <Helmet>
        <script src={process.env.REACT_APP_MOBILE_OK} />
      </Helmet>

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="findpw-title"
        className="fixed inset-0 z-[1400] flex items-center justify-center px-5"
      >
        <button
          type="button"
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
          aria-label="닫기"
        />

        <div
          className="relative z-10 w-full max-w-[340px] rounded-3xl bg-white px-6 pt-10 pb-7 shadow-[0_8px_32px_rgba(0,0,0,0.12)]"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center text-gray-400 transition-colors hover:text-gray-600"
            aria-label="닫기"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          {findPwInputs.successful ? (
            <div className="text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#2EBD36] text-[#2EBD36]">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>

              <h2
                id="findpw-title"
                className="mb-6 text-xl font-bold text-[#2EBD36]"
              >
                임시 비밀번호 발급 완료
              </h2>

              <div className="mb-6 rounded-2xl border border-[#2EBD36]/60 px-5 py-4 text-left">
                <p className="mb-4 text-[15px] leading-relaxed text-gray-700">
                  {findPwInputs.issueMessage ||
                    '임시 비밀번호가 발급되었습니다.'}
                </p>

                {findPwInputs.tempPassword ? (
                  <dl className="text-sm">
                    <dt className="mb-1 font-medium text-gray-500">
                      임시 비밀번호
                    </dt>
                    <dd className="font-semibold tracking-wide break-all text-gray-900">
                      {findPwInputs.tempPassword}
                    </dd>
                  </dl>
                ) : null}

                <p className="mt-4 text-sm leading-relaxed text-gray-500">
                  발급된 임시 비밀번호로 로그인한 뒤, 마이페이지에서 꼭 비밀번호
                  변경을 진행해 주세요.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-5 flex justify-center">
                <img
                  className="h-auto w-[72%] max-w-[220px]"
                  src={LogoImg}
                  alt="로고"
                />
              </div>

              <p
                id="findpw-title"
                className="mb-1 text-center text-[15px] text-gray-500"
              >
                비밀번호를 잊어버리셨나요?
              </p>
              <p className="mb-6 text-center text-sm leading-relaxed text-gray-400">
                회원가입 시 작성하셨던 전화번호로
                <br />
                본인인증을 진행해 주세요.
              </p>

              {findPwInputs.identityErrMsg ? (
                <p className="mb-3 text-center text-sm text-red-600">
                  {findPwInputs.identityErrMsg}
                </p>
              ) : null}

              {findPwInputs.identityConfirm ? (
                <p className="mb-3 text-center text-sm font-medium text-[#2EBD36]">
                  본인인증이 완료되었습니다.
                </p>
              ) : null}

              <button
                type="button"
                disabled={primaryDisabled}
                onClick={HandlePrimaryAction}
                className="flex h-[52px] w-full items-center justify-center rounded-xl bg-[#2EBD36] text-base font-semibold text-white transition-colors hover:bg-[#25a82f] disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {primaryLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default FindPw;
